import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { dashboardApi, type DashboardSummary } from '@/api/dashboardApi';
import { companyApi, type CompanySettings, type DayHours } from '@/api/companyApi';
import { ShopDetailsCard } from '@/components/ShopDetailsCard';
import { notificationsApi, type Notification } from '@/api/notificationsApi';
import { useSignalR } from '@/hooks/useSignalR';

interface RightSidebarProps {
  onTechClick?: () => void;
  onBayClick?: () => void;
  onPartsClick?: () => void;
}

interface WeatherState { temp: number; code: number; isDay: number; }

function wmoIcon(code: number, isDay: number): string {
  if (code === 0)  return isDay ? 'wb_sunny' : 'nights_stay';
  if (code <= 3)   return isDay ? 'partly_cloudy_day' : 'nights_stay';
  if (code <= 48)  return 'foggy';
  if (code <= 67)  return 'rainy';
  if (code <= 77)  return 'ac_unit';
  if (code <= 82)  return 'rainy';
  if (code <= 86)  return 'ac_unit';
  return 'thunderstorm';
}

function wmoColor(code: number, isDay: number): string {
  if (code === 0 && isDay) return 'text-yellow-500';
  if (code <= 3)   return 'text-sky-400';
  if (code <= 48)  return 'text-slate-400';
  if (code <= 82)  return 'text-blue-400';
  if (code <= 86)  return 'text-sky-300';
  return 'text-purple-400';
}

function isShopOpen(weeklyHours: string | undefined): boolean {
  if (!weeklyHours) return true;
  try {
    const days: DayHours[] = JSON.parse(weeklyHours);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = dayNames[new Date().getDay()];
    const entry = days.find((d) => d.day === today);
    if (!entry || !entry.isOpen) return false;
    const now = new Date();
    const [oh, om] = entry.open.split(':').map(Number);
    const [ch, cm] = entry.close.split(':').map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    return nowMins >= oh * 60 + om && nowMins < ch * 60 + cm;
  } catch {
    return true;
  }
}

export function RightSidebar({ onTechClick, onBayClick, onPartsClick }: RightSidebarProps) {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [company, setCompany] = useState<CompanySettings | null>(null);
  const [time, setTime] = useState('');
  const [dateLabel, setDateLabel] = useState('');
  const [weather, setWeather] = useState<WeatherState | null>(null);
  const [geoCity, setGeoCity] = useState<string | null>(null);
  const [holiday, setHoliday] = useState<string | null>(null);
  const [shopOpen, setShopOpen] = useState(true);
  const [activities, setActivities] = useState<Notification[]>([]);
  const { on, off } = useSignalR();

  function refreshTelemetry() {
    dashboardApi.getSummary().then(setData).catch(() => {});
    notificationsApi.list().then((items) => setActivities(items.slice(0, 5))).catch(() => {});
    companyApi.getSettings().then((s) => {
      setCompany(s);
      setShopOpen(isShopOpen(s.weeklyHours));
    }).catch(() => {});
  }

  useEffect(() => {
    refreshTelemetry();
    const interval = setInterval(refreshTelemetry, 120_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    on('NewIntakeReceived', refreshTelemetry);
    return () => off('NewIntakeReceived', refreshTelemetry);
  }, [on, off]);

  useEffect(() => {
    window.addEventListener('wrenchlab:settings-saved', refreshTelemetry);
    return () => window.removeEventListener('wrenchlab:settings-saved', refreshTelemetry);
  }, []);

  // Live clock — updates every minute and recomputes open/closed
  useEffect(() => {
    function tick() {
      const now = new Date();
      setTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      setDateLabel(now.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }));
      setCompany((prev) => {
        if (prev) setShopOpen(isShopOpen(prev.weeklyHours));
        return prev;
      });
    }
    tick();
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  // Geolocation → weather + fallback city + holiday (fire-and-forget, fail silently)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lon } = pos.coords;

        // Weather via Open-Meteo (free, no API key)
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`)
          .then((r) => r.json())
          .then((d) => {
            const cw = d.current_weather;
            setWeather({ temp: cw.temperature, code: cw.weathercode, isDay: cw.is_day });
          })
          .catch(() => {});

        // Reverse geocode — only used if the company profile has no city set
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`)
          .then((r) => r.json())
          .then((d) => {
            const addr = d.address ?? {};
            const cityName = addr.city ?? addr.town ?? addr.village ?? null;
            const region = addr.state_code ?? (addr.state ?? '').slice(0, 2).toUpperCase() ?? null;
            setGeoCity(cityName && region ? `${cityName}, ${region}` : cityName);

            // Holidays via Nager.Date (free, no API key)
            const countryCode = (addr.country_code ?? navigator.language.split('-')[1] ?? 'CA').toUpperCase();
            const year = new Date().getFullYear();
            const today = new Date().toISOString().split('T')[0];
            fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`)
              .then((r) => r.json())
              .then((holidays: { date: string; name: string }[]) => {
                const h = holidays.find((x) => x.date === today);
                if (h) setHoliday(h.name);
              })
              .catch(() => {});
          })
          .catch(() => {});
      },
      undefined,
      { timeout: 6000 },
    );
  }, []);

  const techsAvailable = data?.techsAvailableCount ?? 0;
  const techsTotal = data?.totalActiveTechs ?? 0;
  const techPct = techsTotal > 0 ? Math.round((techsAvailable / techsTotal) * 100) : 0;
  const techsAtCapacity = techsTotal > 0 && techsAvailable === 0;
  const baysOccupied = data?.baysOccupied ?? 0;
  const totalBays = data?.totalBays ?? 0;
  const bayPct = totalBays > 0 ? Math.round((baysOccupied / totalBays) * 100) : 0;
  const baysOverCapacity = baysOccupied > totalBays && totalBays > 0;

  // Prefer profile city/province over geolocation-derived string
  const profileCity = company?.city && company?.province
    ? `${company.city}, ${company.province}`
    : company?.city ?? null;
  const locationLabel = profileCity ?? geoCity;

  const shopName = company?.displayName || company?.companyName || null;

  return (
    <aside className="w-80 lg:border-l border-t lg:border-t-0 border-slate-200 dark:border-border-dark bg-white dark:bg-surface-dark flex flex-col lg:h-screen h-auto lg:flex-shrink-0 overflow-y-auto">
      {/* Shop Details */}
      <ShopDetailsCard />

      {/* Shop Telemetry */}
      <div className="p-6 space-y-4">
        <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400">Shop Telemetry</h3>

        <button
          onClick={onTechClick}
          className={`w-full text-left p-4 rounded-xl border transition-all hover:shadow-md cursor-pointer ${
            techsAtCapacity
              ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 hover:bg-red-100/50 dark:hover:bg-red-500/20'
              : 'bg-slate-50 dark:bg-white/5 border-slate-100 dark:border-border-dark/50 hover:bg-slate-100 dark:hover:bg-white/10'
          }`}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold">Technicians Available</span>
            <span className={`text-xs font-bold ${techsAtCapacity ? 'text-red-600 dark:text-red-400' : 'text-primary'}`}>
              {techsAvailable}/{techsTotal}
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-white/10 h-1.5 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${techsAtCapacity ? 'bg-red-500' : 'bg-primary'}`}
              style={{ width: `${techPct}%` }}
            />
          </div>
          {techsAtCapacity && (
            <p className="text-[10px] text-red-600 dark:text-red-400 mt-2 font-medium">
              ⚠ All technicians at capacity
            </p>
          )}
          {techsTotal === 0 && (
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2">No active technicians</p>
          )}
        </button>

        <button
          onClick={onBayClick}
          className={`w-full text-left p-4 rounded-xl border transition-all hover:shadow-md cursor-pointer ${
            baysOverCapacity
              ? 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 hover:bg-amber-100/50 dark:hover:bg-amber-500/20'
              : 'bg-slate-50 dark:bg-white/5 border-slate-100 dark:border-border-dark/50 hover:bg-slate-100 dark:hover:bg-white/10'
          }`}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold">Bays Occupied</span>
            <span className={`text-xs font-bold ${
              baysOverCapacity ? 'text-amber-600 dark:text-amber-400' : 'text-primary'
            }`}>{baysOccupied}/{totalBays || '—'}</span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-white/10 h-1.5 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                baysOverCapacity ? 'bg-amber-500' : 'bg-primary'
              }`}
              style={{ width: `${Math.min(bayPct, 100)}%` }}
            />
          </div>
          {baysOverCapacity && (
            <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2 font-medium">
              ⚠️ More bays occupied than configured
            </p>
          )}
          {totalBays === 0 && (
            <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2">
              Configure total bays in shop settings
            </p>
          )}
        </button>

        <button
          onClick={onPartsClick}
          className="w-full text-left bg-slate-50 dark:bg-white/5 p-4 rounded-xl border border-slate-100 dark:border-border-dark/50 flex items-center justify-between transition-all hover:shadow-md hover:bg-slate-100 dark:hover:bg-white/10 cursor-pointer"
        >
          <span className="text-xs font-semibold">Parts in Use</span>
          <span className="bg-primary/20 text-primary text-[10px] px-2 py-0.5 rounded-full font-bold">
            Active
          </span>
        </button>
      </div>

      {/* Recent Activity */}
      <div className="p-6 border-t border-slate-200 dark:border-border-dark space-y-4 flex-1">
        <h3 className="font-bold text-xs uppercase tracking-wider text-slate-400">Recent Activity</h3>

        <div className="space-y-6">
          {activities.length > 0 ? (
            activities.slice(0, 3).map((n, i, arr) => {
              const isLast = i === arr.length - 1;
              const dotColors = ['bg-primary', 'bg-blue-500', 'bg-green-500'];
              return (
                <div
                  key={n.notificationId}
                  className={`relative pl-6 ${
                    !isLast
                      ? "before:content-[''] before:absolute before:left-[5px] before:top-[24px] before:bottom-[-24px] before:w-[2px] before:bg-slate-100 dark:before:bg-border-dark"
                      : ''
                  }`}
                >
                  <div
                    className={`absolute left-0 top-1 w-2.5 h-2.5 rounded-full ring-4 ring-white dark:ring-surface-dark ${dotColors[i] ?? 'bg-primary'}`}
                  />
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-bold line-clamp-2">{n.body}</p>
                    <p className="text-[10px] text-slate-500">
                      {n.title}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <>
              <div className="relative pl-6 before:content-[''] before:absolute before:left-[5px] before:top-[24px] before:bottom-[-24px] before:w-[2px] before:bg-slate-100 dark:before:bg-border-dark">
                <div className="absolute left-0 top-1 w-2.5 h-2.5 rounded-full bg-primary ring-4 ring-white dark:ring-surface-dark" />
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-bold">All clear</p>
                  <p className="text-[10px] text-slate-500">No pending notifications</p>
                  <p className="text-[10px] text-slate-400 mt-1">just now</p>
                </div>
              </div>
              <div className="relative pl-6">
                <div className="absolute left-0 top-1 w-2.5 h-2.5 rounded-full bg-green-500 ring-4 ring-white dark:ring-surface-dark" />
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-bold">Shop open</p>
                  <p className="text-[10px] text-slate-500">Activity will appear here</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer — Shop Status */}
      <div className="p-4 bg-slate-50 dark:bg-white/5 border-t border-slate-200 dark:border-border-dark space-y-2.5">

        {/* Holiday strip — only visible when today is a public holiday */}
        {holiday && (
          <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg px-3 py-1.5">
            <span className="material-symbols-outlined text-amber-500 text-sm">celebration</span>
            <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 truncate">{holiday}</p>
          </div>
        )}

        {/* Status row */}
        <div className="flex items-center gap-3">
          <span className={`material-symbols-outlined text-2xl flex-shrink-0 ${weather ? wmoColor(weather.code, weather.isDay) : 'text-yellow-500'}`}>
            {weather ? wmoIcon(weather.code, weather.isDay) : 'wb_sunny'}
          </span>
          <div className="flex-1 min-w-0">
            {/* Line 1: temp + open/closed status + time */}
            <div className="flex items-center justify-between gap-1">
              <div className="flex items-center gap-1.5 min-w-0">
                {weather && (
                  <span className="text-xs font-bold leading-tight">{Math.round(weather.temp)}°C</span>
                )}
                <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${shopOpen ? 'bg-green-500' : 'bg-slate-400'}`} />
                <span className="text-xs font-bold leading-tight truncate">
                  {shopOpen ? 'Shop Open' : 'Shop Closed'}
                </span>
              </div>
              <p className="text-xs font-bold tabular-nums text-slate-600 dark:text-slate-300 flex-shrink-0">{time}</p>
            </div>
            {/* Line 2: shop name + location + date */}
            <p className="text-[10px] text-slate-400 truncate mt-0.5">
              {shopName ? `${shopName} · ` : ''}{locationLabel ? `${locationLabel} · ` : ''}{dateLabel}
            </p>
          </div>
        </div>

      </div>
    </aside>
  );
}
